import React, { useEffect, useMemo, useState } from 'react';
import { t } from 'ttag';
import { filter, sortBy } from 'lodash';
import {
  BaseDialog,
  Checkbox,
  DisconnectedIcon,
  Table,
  Typography,
} from '@coolremote/shared';
import { useStoreActions } from '@models/RootStore';
import {
  useGetSiteModbusUnitsWithDevices,
  useUpdateModbusUnit,
} from '@hooks/useModbusDevicesApi';
import styles from './ManuallySetModbusUnits.module.css';

interface IProps {
  device: any;
  line: any;
  closeModal: () => void;
  fetchData: () => void;
}

const lineLabel = (line: any) => {
  if (isNaN(line)) {
    return t`unknown line`;
  }
  return line == 0 ? 'IP Modbus' : `L${line} Modbus`;
};

// Bare line label (no "Modbus" suffix) — used in the dialog title, e.g. "Line L3".
const lineOnly = (line: any) => {
  if (isNaN(line)) {
    return t`unknown line`;
  }
  return line == 0 ? 'IP' : `L${line}`;
};

const ManuallySetModbusUnits: React.FC<IProps> = ({
  device,
  line,
  closeModal,
  fetchData,
}) => {
  const { addMessage } = useStoreActions((action: any) => action.errorMessage);

  const [units, setUnits] = useState<Record<string, any>>({});
  // Units whose isVisible differs from what was loaded.
  const [dirtyIds, setDirtyIds] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const { data: siteModbusUnits, refetch } = useGetSiteModbusUnitsWithDevices(
    device?.site
  ) as unknown as { data: Record<string, any> | null; refetch: () => void };

  const { mutate: updateModbusUnit } = useUpdateModbusUnit({
    onError: (e: any) => addMessage({ message: e?.message }),
  });

  useEffect(() => {
    setUnits(siteModbusUnits || {});
    setDirtyIds({});
  }, [siteModbusUnits]);

  // The App Unit Name is stored on `name`. Unlike createModbusDevice, the
  // backend's createModbusUnit never backfills `title` from `name`, and the
  // client only ever sends `name` — so `title` is undefined on every modbus
  // unit and can't be relied on for display or sorting.
  const appUnitName = (entry: any) => entry?.title || entry?.name || '';

  // This device's engaged units on the selected line, mirroring the BACnet dialog.
  const rows = useMemo(() => {
    return sortBy(
      filter(
        units,
        (entry: any) =>
          String(entry.device) === String(device?.id) &&
          entry.line == line &&
          entry.isEngaged
      ),
      [appUnitName]
    );
  }, [units, device, line]);

  // Checkbox passes back its `value` prop, so `value={id}` gives us the unit id.
  const toggleVisible = (id: string) => {
    const unit = units[id];
    if (!unit) {
      return;
    }
    const nextVisible = !unit.isVisible;
    setUnits({ ...units, [id]: { ...unit, isVisible: nextVisible } });
    setDirtyIds((prev) => {
      const next = { ...prev };
      // Back to the loaded value → no longer pending.
      if (nextVisible === (siteModbusUnits?.[id]?.isVisible ?? true)) {
        delete next[id];
      } else {
        next[id] = true;
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    for (const id of Object.keys(dirtyIds)) {
      const unit = units[id];
      if (!unit) {
        continue;
      }
      await updateModbusUnit({
        modbusUnitId: id,
        data: { isVisible: unit.isVisible },
      });
    }
    setSaving(false);
    refetch();
    fetchData();
    closeModal();
  };

  const canUpdate = !!device?.permissions?.canUpdate;

  return (
    <BaseDialog
      open
      onClose={closeModal}
      onConfirm={handleSave}
      title={`${t`Edit Modbus Device`} - ${t`Line`} ${lineOnly(line)}, ${t`Device`} ${device?.serial ?? '—'}`}
      confirmLabel={t`Save`}
      cancelLabel={t`Cancel`}
      size="fullWidth"
      confirmDisabled={Object.keys(dirtyIds).length === 0}
      loading={saving}
    >
      <div className={styles.card}>
        <Typography className={styles.cardTitle}>
          {t`Modbus Devices`} - {t`Line`} {lineLabel(line)}
        </Typography>
        <div className={styles.tableScroll}>
          <Table
            data={rows}
            getRowKey={(row: any, index: number) => row.id ?? index}
            emptyMessage={t`No Modbus devices bound on this line.`}
            columns={[
              {
                id: 'modbusDeviceName',
                title: t`Modbus Device`,
                width: '16%',
                strong: true,
                render: (row: any) => (
                  <div className={styles.nameCell}>
                    <span>
                      {row.modbusDeviceName || '—'}
                    </span>
                    {!row.isConnected && <DisconnectedIcon width={18} height={18} />}
                  </div>
                ),
              },
              {
                id: 'slaveId',
                title: t`Device ID`,
                width: '13%',
                // Modbus Slave ID, set on the modbus device's connection params.
                render: (row: any) => row.slaveId ?? '—',
              },
              {
                id: 'title',
                title: t`App Name`,
                width: '20%',
                render: (row: any) => appUnitName(row) || '—',
              },
              {
                id: 'line',
                title: t`Line`,
                width: '9%',
                render: (row: any) => lineLabel(row.line),
              },
              {
                id: 'vendorName',
                title: t`Manufacturer`,
                width: '16%',
                render: (row: any) => row.vendorName || '—',
              },
              {
                id: 'modelName',
                title: t`Model`,
                width: '14%',
                render: (row: any) => row.modelName || '—',
              },
              {
                id: 'isVisible',
                title: t`Visible`,
                width: '6%',
                align: 'center',
                render: (row: any) => (
                  <Checkbox
                    size="small"
                    disabled={!canUpdate}
                    checked={row.isVisible}
                    value={row.id}
                    onChange={toggleVisible}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>
      {!canUpdate && (
        <Typography variant="meta" color="muted">
          {t`You do not have permission to change these devices.`}
        </Typography>
      )}
    </BaseDialog>
  );
};

export default ManuallySetModbusUnits;
